import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { MagnifyingGlass, ShieldCheck } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import OpenInTabLink from "../components/common/OpenInTabLink";

const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const Products = ({ user, logout }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [newProduct, setNewProduct] = useState({
    brand_id: "",
    name: "",
    category: "camera",
    description: "",
    price: "",
    image_url: "",
    features: ""
  });

  const fetchProducts = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.category = filter;
      if (search) params.search = search;
      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error("Failed to fetch brands");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchProducts();
    fetchBrands();
  }, [fetchProducts, fetchBrands]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        features: newProduct.features.split(',').map(f => f.trim()).filter(f => f)
      };
      await axios.post(`${API}/products?requester_role=${user?.role || ""}`, productData);
      toast.success("Product added successfully!");
      setShowAddProduct(false);
      setNewProduct({
        brand_id: "",
        name: "",
        category: "camera",
        description: "",
        price: "",
        image_url: "",
        features: ""
      });
      fetchProducts();
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  const filteredProducts = products;

  const catalogStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "SwifTech India Security Product Catalog",
    "itemListElement": products.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `${SITE_ORIGIN}/products/${p.product_id}`,
      "name": p.name
    }))
  };

  return (
    <>
      <SEO
        title="Security Product Catalog — CCTV, Alarms & Access Control | SwifTech India"
        description="Browse top-brand security systems: 4K CCTV cameras, smart alarms, biometric access control. Trusted brands, expert installation across India."
        canonical={`${SITE_ORIGIN}/products`}
        keywords="CCTV India, security cameras, biometric access control, smart alarm, home security, business security, SwifTech India"
        structuredData={catalogStructuredData}
      />
    <div className="flex min-h-screen bg-slate-50">
      {user && <Sidebar user={user} logout={logout} />}

      <div className={`flex-1 min-w-0 overflow-x-hidden ${user ? 'ml-64' : ''}`}>
        {/* Public header when not logged in */}
        {!user && (
          <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <ShieldCheck size={28} weight="duotone" className="text-[#002FA7]" />
                <span className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>SwifTech India</span>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid="public-login-btn" onClick={() => navigate('/auth')}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium text-sm transition-colors duration-300">
                  Login / Register
                </button>
              </div>
            </div>
          </header>
        )}
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                {user ? 'Products' : 'Security Product Catalog'}
              </h1>
              <p className="text-base leading-relaxed text-slate-700">
                {user ? 'Manage your security product catalog' : 'Browse multi-brand security systems from top manufacturers'}
              </p>
            </div>
            {/* RBAC: backend enforces via ?requester_role=. BDE intentionally NOT in this list. */}
            {(user?.role === 'admin' || user?.role === 'vendor' || user?.role === 'manager' || user?.role === 'bdm') && (
              <button
                data-testid="add-product-btn"
                onClick={() => setShowAddProduct(!showAddProduct)}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300"
              >
                {showAddProduct ? 'Cancel' : 'Add Product'}
              </button>
            )}
          </div>

          {showAddProduct && (
            <div data-testid="add-product-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Add New Product</h3>
              <form onSubmit={handleAddProduct} className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Product Name</label>
                  <input
                    data-testid="product-name-input"
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Brand</label>
                  <select
                    data-testid="product-brand-select"
                    value={newProduct.brand_id}
                    onChange={(e) => setNewProduct({...newProduct, brand_id: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands.map(brand => (
                      <option key={brand.brand_id} value={brand.brand_id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Category</label>
                  <select
                    data-testid="product-category-select"
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                  >
                    <option value="camera">Camera</option>
                    <option value="alarm">Alarm</option>
                    <option value="access_control">Access Control</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Price</label>
                  <input
                    data-testid="product-price-input"
                    type="number"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Description</label>
                  <textarea
                    data-testid="product-description-input"
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    rows="3"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Image URL</label>
                  <input
                    data-testid="product-image-input"
                    type="url"
                    value={newProduct.image_url}
                    onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Features (comma-separated)</label>
                  <input
                    data-testid="product-features-input"
                    type="text"
                    value={newProduct.features}
                    onChange={(e) => setNewProduct({...newProduct, features: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    placeholder="Feature 1, Feature 2, Feature 3"
                  />
                </div>
                <div className="col-span-2">
                  <button
                    data-testid="submit-product-btn"
                    type="submit"
                    className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300"
                  >
                    Add Product
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mb-6">
            <div className="relative mb-4">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="product-search-input" type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products by name or description..."
                className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 pl-10 w-full outline-none" />
            </div>
            <div className="flex gap-4">
            <button
              data-testid="filter-all-btn"
              onClick={() => setFilter("")}
              className={`rounded-none px-4 py-2 font-medium transition-colors duration-300 ${!filter ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50'}`}
            >
              All
            </button>
            <button
              data-testid="filter-camera-btn"
              onClick={() => setFilter("camera")}
              className={`rounded-none px-4 py-2 font-medium transition-colors duration-300 ${filter === 'camera' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50'}`}
            >
              Cameras
            </button>
            <button
              data-testid="filter-alarm-btn"
              onClick={() => setFilter("alarm")}
              className={`rounded-none px-4 py-2 font-medium transition-colors duration-300 ${filter === 'alarm' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50'}`}
            >
              Alarms
            </button>
            <button
              data-testid="filter-access-btn"
              onClick={() => setFilter("access_control")}
              className={`rounded-none px-4 py-2 font-medium transition-colors duration-300 ${filter === 'access_control' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-50'}`}
            >
              Access Control
            </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div data-testid="products-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.product_id}
                  data-testid={`product-card-${index}`}
                  onClick={() => navigate(`/products/${product.product_id}`)}
                  className="bg-white border border-slate-200 rounded-none overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                >
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6 relative">
                    <OpenInTabLink to={`/products/${product.product_id}`} testId={`product-open-tab-${index}`} size={14} />
                    <div className="rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 inline-block mb-3">
                      {product.category.replace('_', ' ')}
                    </div>
                    <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                    <div className="text-2xl font-bold text-[#002FA7] mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
                      ₹{product.price?.toLocaleString('en-IN')}
                    </div>
                    {product.features && product.features.length > 0 && (
                      <div className="space-y-1">
                        {product.features.slice(0, 3).map((feature) => (
                          <div key={feature} className="text-xs text-slate-600">• {feature}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Products;
